/**
 * ramp — bake a ParticleConfig's over-life color/size/alpha curves into a
 * 4-stop ParticleRamp for the GPU render material.
 *
 * The CPU emitter samples arbitrary-length keyframe arrays per frame; the GPU
 * material evaluates a fixed 4-stop ramp in the shader. We resample the config
 * curves at four normalized times (0, 1/3, 2/3, 1) so the common "fade in,
 * hold, fade out" and "shrink over life" shapes carry through faithfully.
 */

import * as THREE from 'three/webgpu';
import type {
  ParticleConfig, CurveKeyframe, GradientKeyframe,
} from '../types.js';
import type { ParticleRamp } from './types.js';

const RAMP_TIMES: [number, number, number, number] = [0, 1 / 3, 2 / 3, 1];

/** Linear-sample a numeric curve at `t` (matches the CPU `_sampleCurve`). */
function sampleCurve(keys: CurveKeyframe[] | undefined, t: number, fallback: number): number {
  if (!keys || keys.length === 0) return fallback;
  if (t <= keys[0].time) return keys[0].value;
  const last = keys[keys.length - 1];
  if (t >= last.time) return last.value;
  for (let i = 0; i < keys.length - 1; i++) {
    if (t >= keys[i].time && t <= keys[i + 1].time) {
      const span = keys[i + 1].time - keys[i].time;
      const f = span > 0 ? (t - keys[i].time) / span : 0;
      return keys[i].value + f * (keys[i + 1].value - keys[i].value);
    }
  }
  return last.value;
}

const _c = new THREE.Color();

/** Linear-sample a color gradient at `t` (matches the CPU `_sampleGradient`). */
function sampleGradient(keys: GradientKeyframe[] | undefined, t: number, fallback: THREE.Color): THREE.Color {
  if (!keys || keys.length === 0) return _c.copy(fallback);
  if (t <= keys[0].time) return _c.copy(keys[0].color);
  const last = keys[keys.length - 1];
  if (t >= last.time) return _c.copy(last.color);
  for (let i = 0; i < keys.length - 1; i++) {
    if (t >= keys[i].time && t <= keys[i + 1].time) {
      const span = keys[i + 1].time - keys[i].time;
      const f = span > 0 ? (t - keys[i].time) / span : 0;
      return _c.copy(keys[i].color).lerp(keys[i + 1].color, f);
    }
  }
  return _c.copy(last.color);
}

/**
 * Build a 4-stop ParticleRamp from a ParticleConfig.
 *
 * - Color: from `colorOverLifetime`, else flat `startColor`.
 * - Alpha: from `alphaOverLifetime`. If neither alpha nor color gradient is
 *   given, a gentle fade-out in the last 20% is applied (mirrors the CPU
 *   emitter's documented default behavior).
 * - Size:  multiplier from `sizeOverLifetime`, else flat 1.
 */
export function rampFromConfig(config: ParticleConfig): ParticleRamp {
  const startColor = new THREE.Color(config.startColor);
  const hasAlphaCurve = !!config.alphaOverLifetime && config.alphaOverLifetime.length > 0;
  const hasColorGradient = !!config.colorOverLifetime && config.colorOverLifetime.length > 0;

  const colors = RAMP_TIMES.map((t) => {
    const c = hasColorGradient
      ? sampleGradient(config.colorOverLifetime, t, startColor)
      : startColor;
    let a: number;
    if (hasAlphaCurve) {
      a = sampleCurve(config.alphaOverLifetime, t, 1);
    } else if (!hasColorGradient) {
      // Default soft fade in last 20% of life.
      a = t < 0.8 ? 1 : 1 - (t - 0.8) / 0.2;
    } else {
      a = 1;
    }
    return new THREE.Vector4(c.r, c.g, c.b, Math.max(0, Math.min(1, a)));
  }) as [THREE.Vector4, THREE.Vector4, THREE.Vector4, THREE.Vector4];

  const sizes = RAMP_TIMES.map((t) => sampleCurve(config.sizeOverLifetime, t, 1)) as
    [number, number, number, number];

  return {
    times: [...RAMP_TIMES] as [number, number, number, number],
    colors,
    sizes,
  };
}
